import { twMerge } from "tailwind-merge";

import { asString, getSectionTone, toTitle } from "./sectionHelpers";

export type RenderOptions = {
  noDottedLines?: boolean;
  forceBulletColor?: string;
  noQuoteMargin?: boolean;
  hideQuoteTitle?: boolean;
};

export const renderSectionContent = (
  key: string,
  value: any,
  options?: RenderOptions
): JSX.Element => {
  const tone = getSectionTone(key);

  if (tone.isQuote) {
    return (
      <blockquote
        className={twMerge(
          "rounded-[4px] border-0 border-l-[3px] border-solid border-smart-teal bg-[#E1F8F8] px-4 py-4 text-[15px] italic leading-7 text-[#1A1A1A] md:px-5 md:py-5 md:text-[16px]",
          !options?.noQuoteMargin && "mx-[40px]"
        )}
      >
        {!options?.hideQuoteTitle && (
          <span className="mb-2 block text-[11px] not-italic font-medium uppercase tracking-[0.06em] text-smart-teal">
            {tone.title}
          </span>
        )}
        {asString(value)}
      </blockquote>
    );
  }

  if (Array.isArray(value)) {
    return (
      <ul className="m-0 list-none space-y-3 p-0">
        {value.map((line: any, index: number) => (
          <li
            key={`${asString(line)}-${index}`}
            className={twMerge(
              "relative pl-6 text-[14px] leading-6 text-[#404040] md:text-[15px] md:leading-7",
              !options?.noDottedLines &&
                "border-0 border-b border-dashed border-[#E8E8E8] pb-3 last:border-b-0 last:pb-0"
            )}
          >
            <span
              className={twMerge(
                "absolute left-1.5 top-[11px] h-1.5 w-1.5 rounded-full",
                options?.forceBulletColor ||
                  tone.bulletClassName ||
                  "bg-smart-teal"
              )}
            />
            {asString(line)}
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === "object" && value !== null) {
    return (
      <div className="space-y-4">
        {Object.entries(value).map(([subKey, subValue]) => {
          const subKeyTone = getSectionTone(subKey);

          return (
            <div key={subKey} className="space-y-2">
              <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.04em] text-[#808080]">
                <span>{toTitle(subKey)}</span>
                {subKeyTone.tag && (
                  <span
                    className={twMerge(
                      "rounded-full px-2 py-[1px] text-[10px] font-semibold uppercase leading-none",
                      subKeyTone.tagClassName
                    )}
                  >
                    {subKeyTone.tag}
                  </span>
                )}
              </div>
              {renderSectionContent(subKey, subValue, options)}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <p className="m-0 text-[14px] leading-7 text-[#404040] md:text-[15px] md:leading-8">
      {asString(value)}
    </p>
  );
};
